import { ContentType, Route, resolveLocalizedValue, sentryLogger } from "@robe/core";
import { LinkableContent } from "../ContentLink";
import { ContentLinkContextData, ID_REGEX, logLinkError, SLUG_REGEX } from "./utils";

const validateRouteSlugPair = (route: Route, id?: string | number | null, slug?: string | null) => {
  if ((ID_REGEX.exec(route.fromPath) || ID_REGEX.exec(route.toPath)) && !id) {
    logLinkWarn(
      `Content ID: ${id}, Content Slug: ${slug}, Route ${route.fromPath} -> ${route.toPath} contains id placeholder, but id value is falsy.`
    );

    return false;
  }

  if ((SLUG_REGEX.exec(route.fromPath) || SLUG_REGEX.exec(route.toPath)) && !slug) {
    logLinkWarn(
      `Content ID: ${id}, Content Slug: ${slug}, Path ${route.fromPath} -> ${route.toPath} contains slug placeholder, but slug value is falsy.`
    );

    return false;
  }

  return true;
};

interface RoutesSlugPairFromContentProps {
  content: LinkableContent;
  contentType: ContentType;
  contentLocale: string;
}

export interface RouteSlugPair {
  slug?: string | null;
  route?: Route | null;
}

export const routeSlugPairFromContent = (
  { core: { currentLocale, locales } }: ContentLinkContextData,
  { content, contentType, contentLocale }: RoutesSlugPairFromContentProps
): { slug?: string | null; route?: Route | null } | null => {
  if (!contentType?.routes || Object.keys(contentType.routes).length === 0) {
    logLinkError(`Failed to create a link, content type "${contentType?.name ?? ""}" is not routable. Content id: ${content.id}`, {
      contentType,
      content,
      contentLocale,
    });

    return null;
  }

  let route: Route | undefined;
  const slug =
    typeof content.slug === "string"
      ? content.slug
      : contentLocale
        ? ((content.slug &&
            (resolveLocalizedValue(contentLocale, locales)(content.slug)?.value ??
              resolveLocalizedValue(currentLocale, locales)(content.slug)?.value)) ??
          null)
        : null;

  if (slug === "") {
    logLinkWarn(`Content ${content.id} has empty string as a slug.`);
  }

  if (contentLocale) {
    route = resolveLocalizedValue(contentLocale, locales)(contentType.routes)?.value;
  }

  if (!route) {
    return null;
  }

  if (validateRouteSlugPair(route, content.id, slug)) {
    return { slug, route };
  }

  return null;
};

const logLinkWarn = (error: string) => {
  sentryLogger({
    type: "debug",
    message: `<Link> or useLink: ${error}`,
    debugBlock: "link",
  });
};
